/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { DynamicFormProps } from '@/shared/components/DynamicForm/DynamicForm'
import IMAGE_SLOTS_KEYS from '@/shared/store/imageSlots.model'
import { CONTRACT_TYPES, POSITIONS } from '../types/employment.model'
import { currencySymbol } from '@/shared/utils/price'

export const useEmployeeFormConfig = () => {
  const { t } = useTranslation()

  const formConfig: any = {
    id: 'employee-form',
    fields: [
      {
        type: 'input-group',
        // Top-aligned, not bottom: the avatar is taller than the fields beside
        // it, so `flex-end` pushed the whole column down and opened a gap
        // between the modal title and the first input.
        alignItems: 'flex-start',
        inputs: [
          {
            name: 'avatar',
            type: 'image',
            imageShape: 'circle',
            modalId: IMAGE_SLOTS_KEYS.EMPLOYEE_AVATAR,
            label: t('TABLE.EMPLOYEES.FORM.AVATAR')
          },
          {
            type: 'input-group',
            inputGroupDirection: 'column',
            inputs: [
              {
                name: 'formDescription',
                label: t('TABLE.EMPLOYEES.FORM.DESCRIPTION'),
                type: 'label'
              },
              {
                name: 'displayName',
                label: t('TABLE.EMPLOYEES.FORM.NAME'),
                placeholder: t('TABLE.EMPLOYEES.FORM.NAME_PLACEHOLDER'),
                type: 'text',
                focused: true,
                required: true,
                validation: z
                  .string()
                  .min(1, t('TABLE.EMPLOYEES.FORM.NAME_REQUIRED'))
              },
              {
                type: 'input-group',
                inputs: [
                  {
                    name: 'email',
                    label: t('TABLE.EMPLOYEES.FORM.EMAIL'),
                    placeholder: t('TABLE.EMPLOYEES.FORM.EMAIL_PLACEHOLDER'),
                    type: 'email',
                    focused: true,
                    required: true,
                    validation: z
                      .string()
                      .min(1, t('TABLE.EMPLOYEES.FORM.EMAIL_REQUIRED'))
                      .email(t('TABLE.EMPLOYEES.FORM.EMAIL_INVALID'))
                  },
                  {
                    name: 'phoneNumber',
                    label: t('TABLE.EMPLOYEES.FORM.PHONE'),
                    placeholder: t('TABLE.EMPLOYEES.FORM.PHONE_PLACEHOLDER'),
                    type: 'text',
                    focused: true,
                    required: true,
                    validation: z
                      .string()
                      .min(1, t('TABLE.EMPLOYEES.FORM.PHONE_REQUIRED'))
                      .regex(
                        /^\+?[0-9]{7,15}$/,
                        t('TABLE.EMPLOYEES.FORM.PHONE_INVALID')
                      )
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        name: 'employmentDescription',
        label: t('TABLE.EMPLOYEES.FORM.EMPLOYMENT_DESCRIPTION'),
        type: 'label'
      },
      {
        type: 'input-group',
        inputs: [
          {
            name: 'position',
            label: t('TABLE.EMPLOYEES.FORM.POSITION'),
            placeholder: t('TABLE.EMPLOYEES.FORM.POSITION_PLACEHOLDER'),
            type: 'select',
            focused: true,
            options: POSITIONS.map(position => ({
              label: t(`TABLE.EMPLOYEES.POSITIONS.${position.toUpperCase()}`),
              value: position
            }))
          },
          {
            name: 'contractType',
            label: t('TABLE.EMPLOYEES.FORM.CONTRACT_TYPE'),
            placeholder: t('TABLE.EMPLOYEES.FORM.CONTRACT_TYPE_PLACEHOLDER'),
            type: 'select',
            focused: true,
            options: CONTRACT_TYPES.map(contract => ({
              label: t(`TABLE.EMPLOYEES.CONTRACTS.${contract.toUpperCase()}`),
              value: contract
            }))
          }
        ]
      },
      {
        type: 'input-group',
        inputs: [
          {
            name: 'hourlyRate',
            label: t('TABLE.EMPLOYEES.FORM.HOURLY_RATE'),
            placeholder: t('TABLE.EMPLOYEES.FORM.HOURLY_RATE_PLACEHOLDER'),
            type: 'text',
            focused: true,
            endAdornment: currencySymbol(),
            validation: z
              .preprocess(value => {
                if (typeof value === 'string') {
                  // Comma is the decimal separator people type here
                  const cleaned = value
                    .replace(',', '.')
                    .replace(/[^0-9.]+/g, '')
                  return cleaned === '' ? undefined : Number(cleaned)
                }
                if (typeof value === 'number') return value
                return undefined
              }, z.number().min(0.01).max(99999))
              .optional()
          },
          {
            name: 'weeklyHoursLimit',
            label: t('TABLE.EMPLOYEES.FORM.WEEKLY_HOURS'),
            placeholder: t('TABLE.EMPLOYEES.FORM.WEEKLY_HOURS_PLACEHOLDER'),
            type: 'text',
            focused: true,
            endAdornment: 'h',
            validation: z
              .preprocess(value => {
                if (typeof value === 'string') {
                  const cleaned = value.replace(/[^0-9]+/g, '')
                  return cleaned === '' ? undefined : Number(cleaned)
                }
                if (typeof value === 'number') return value
                return undefined
              }, z.number().min(1).max(168))
              .optional()
          }
        ]
      }
    ]
  }

  return formConfig as DynamicFormProps
}
